import { useState, useEffect, useCallback } from "react";
import { db } from "../../db";
import { Btn, Empty, Spinner, BlockHead, Chips, Selo, MiniBar, Check, Explica } from "../../components/primitives";

// ─────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────

const IMPLANTACAO_STATUS_CFG = {
  em_andamento: { label: "Em andamento", tone: "warn", bg: "var(--warn-soft)",    color: "var(--warn-ink)" },
  concluida:    { label: "Concluída",    tone: "ok",   bg: "var(--success-soft)", color: "var(--success-ink)" },
  pausada:      { label: "Pausada",      tone: "off",  bg: "var(--surface-3)", color: "var(--muted)" },
};

const CHECKLIST_ITEMS = [
  { id: "workflow_importado",     label: "Workflow importado no n8n"                       },
  { id: "variaveis_configuradas", label: "Variáveis de ambiente configuradas no EasyPanel"  },
  { id: "evolution_conectada",    label: "Instância Evolution criada e conectada"           },
  { id: "webhook_configurado",    label: "Webhook configurado no n8n"                      },
  { id: "supabase_acessivel",     label: "Tabela chatbot_sessions acessível"                },
  { id: "primeiro_teste",         label: "Primeiro teste de mensagem realizado"             },
  { id: "secretaria_treinada",    label: "Secretária treinada"                             },
  { id: "cliente_aprovado",       label: "Cliente treinado e aprovado"                     },
];

const CHECKLIST_SEED = CHECKLIST_ITEMS.map(({ id, label }) => ({
  id, label, concluido: false, responsavel: null, data: null,
}));

const HOMOLOGACAO_LABELS = {
  dados_medico:      "Dados do médico preenchidos",
  secretaria:        "Secretária configurada com WhatsApp",
  convenios:         "Convênios aceitos e recusados definidos",
  unidades:          "Pelo menos uma unidade com horário cadastrado",
  mensagens_core:    "Mensagens obrigatórias preenchidas",
  followup_config:   "Follow-up configurado",
  sandbox_teste:     "Fluxo testado no LAB/Sandbox",
  fluxo_completo:    "Fluxo completo validado",
  secretaria_alerta: "Secretária informada sobre o funcionamento do bot",
  cliente_aprovado:  "Cliente aprovou o fluxo",
};

const EVENTO_TIMELINE_CFG = {
  implantacao_iniciada:       { tone: "info", icone: "🚀", label: "Implantação iniciada",   cor: "var(--cat-violet-ink)" },
  implantacao_item_concluido: { tone: "info", icone: "✔",  label: "Item concluído",         cor: "var(--info-ink)" },
  implantacao_concluida:      { tone: "ok", icone: "🏁", label: "Implantação concluída",  cor: "var(--success-ink)" },
  artefato_gerado:            { tone: "off", icone: "⬇",  label: "Artefato gerado",        cor: "var(--cat-teal-ink)" },
  cliente_criado:             { tone: "info", icone: "✨", label: "Cliente criado",         cor: "var(--info-ink)" },
  exportacao_gerada:          { tone: "off", icone: "⬇",  label: "Exportação gerada",      cor: "var(--success-ink)" },
  homologacao_concluida:      { tone: "ok", icone: "✅", label: "Homologação concluída",  cor: "var(--success)" },
};

const EVENTOS_IMPLANTACAO = new Set([
  "implantacao_iniciada", "implantacao_item_concluido",
  "implantacao_concluida", "artefato_gerado",
]);

// ─────────────────────────────────────────────────────────────
// Utilitários
// ─────────────────────────────────────────────────────────────

function downloadMd(content, filename) {
  const blob = new Blob([content], { type: "text/markdown" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(ts) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

function fmtDateTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return (
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers de layout
// ─────────────────────────────────────────────────────────────

function Section({ title, sub, children }) {
  return (
    <div>
      <BlockHead title={title} sub={sub} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

function LoadingInline() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 0", color: "var(--muted)" }}>
      <Spinner size={18} />
      <span style={{ fontSize: 13 }}>Carregando...</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChecklistItem
// ─────────────────────────────────────────────────────────────

function ChecklistItem({ item, onItemChange }) {
  const [resp, setResp] = useState(item.responsavel || "");

  useEffect(() => {
    setResp(item.responsavel || "");
  }, [item.responsavel]);

  function handleRespSave() {
    const trimmed = resp.trim();
    if (trimmed !== (item.responsavel || "")) {
      onItemChange({ ...item, responsavel: trimmed || null });
    }
  }

  return (
    <div className={`klist-row${item.concluido ? " done" : ""}`} style={{ flexWrap: "wrap" }}>
      <Check
        on={item.concluido}
        label={item.label}
        onClick={() => onItemChange({
          ...item,
          concluido: !item.concluido,
          data:      !item.concluido ? new Date().toISOString() : null,
        })}
      />
      <span className="txt">{item.label}</span>
      {item.concluido && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {item.data && <span className="aside">{fmtDate(item.data)}</span>}
          <input
            className="input"
            value={resp}
            onChange={(e) => setResp(e.target.value)}
            onBlur={handleRespSave}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            placeholder="responsável"
            aria-label={`Responsável: ${item.label}`}
            style={{ width: 140, height: 30, fontSize: 12.5, padding: "2px 10px" }}
          />
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChecklistImplantacao
// ─────────────────────────────────────────────────────────────

function ChecklistImplantacao({ items, onItemChange }) {
  const done  = items.filter((i) => i.concluido).length;
  const total = items.length;

  return (
    <div className="klist">
      <div className="klist-head">
        <span className="eyebrow">{done}/{total} etapas concluídas</span>
        <MiniBar done={done} total={total} width={110} />
      </div>
      {items.map((item) => (
        <ChecklistItem key={item.id} item={item} onItemChange={onItemChange} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ArtefatosPanel
// ─────────────────────────────────────────────────────────────

function ArtefatosPanel({ cliente, clienteId, implantacao, onArtefatoGerado }) {
  const [loadingReadme, setLoadingReadme] = useState(false);

  async function gerarReadme() {
    setLoadingReadme(true);
    try {
      const [convenios, unidades] = await Promise.all([
        db("fluxomed_convenios", "GET", null, `?cliente_id=eq.${clienteId}&order=created_at`),
        db("fluxomed_unidades",  "GET", null, `?cliente_id=eq.${clienteId}&ativo=eq.true&order=created_at`),
      ]);

      const aceitos   = (convenios || []).filter((c) => c.aceito).map((c) => c.nome);
      const recusados = (convenios || []).filter((c) => !c.aceito).map((c) => c.nome);
      const agora     = new Date().toLocaleDateString("pt-BR");

      const unidadesStr = (unidades || []).map((u) => [
        `### ${u.nome} (${u.key})`,
        u.regiao    ? `- Região: ${u.regiao}` : null,
        u.endereco  ? `- Endereço: ${u.endereco}` : null,
        (u.dia_semana && u.horario)
          ? `- Atendimento: ${u.dia_semana} às ${u.horario}${u.quinzenal ? " (quinzenal)" : ""}`
          : null,
      ].filter(Boolean).join("\n")).join("\n\n");

      const sCfg = IMPLANTACAO_STATUS_CFG[implantacao.status] || IMPLANTACAO_STATUS_CFG.em_andamento;

      const md = [
        `# FluxoMed — ${cliente.nome}`,
        `> Gerado em: ${agora} | Template: ${cliente.origem_template || "—"} | Instância: ${cliente.instancia_evolution || "—"}`,
        "",
        "## Médico",
        `- Nome: ${cliente.medico_nome || "—"}`,
        `- Especialidade: ${cliente.medico_especialidade || "—"}`,
        `- CRM: ${cliente.medico_crm || "—"}`,
        "",
        "## Secretária",
        `- Nome: ${cliente.secretaria_nome || "—"}`,
        `- WhatsApp: ${cliente.secretaria_whatsapp || "—"}`,
        `- Horário: ${cliente.secretaria_horario_inicio || "08:00"} às ${cliente.secretaria_horario_fim || "18:00"}`,
        "",
        "## Convênios aceitos",
        aceitos.length ? aceitos.map((c) => `- ${c}`).join("\n") : "- Nenhum cadastrado",
        "",
        "## Convênios não atendidos",
        recusados.length ? recusados.map((c) => `- ${c}`).join("\n") : "- Nenhum cadastrado",
        "",
        "## Unidades",
        unidadesStr || "Nenhuma unidade cadastrada.",
        "",
        "## Follow-up",
        `- Ativo: ${cliente.followup_ativo !== false ? "Sim" : "Não"}`,
        `- Delay: ${cliente.followup_delay_horas ?? 4}h`,
        `- Janela: ${cliente.followup_janela_inicio || "08:00"} às ${cliente.followup_janela_fim || "20:00"}`,
        `- Timeout terminal: ${cliente.followup_timeout_terminal_horas ?? 4}h`,
        `- Timeout follow-up: ${cliente.followup_timeout_followup_horas ?? 24}h`,
        "",
        "## Configuração técnica",
        `- Projeto Supabase: ${cliente.supabase_project || "—"}`,
        `- Instância Evolution: ${cliente.instancia_evolution || "—"}`,
        "",
        "## Status de implantação",
        `- Status: ${sCfg.label}`,
        implantacao.responsavel  ? `- Responsável: ${implantacao.responsavel}` : null,
        implantacao.data_inicio  ? `- Início: ${fmtDate(implantacao.data_inicio)}` : null,
        implantacao.data_conclusao ? `- Conclusão: ${fmtDate(implantacao.data_conclusao)}` : null,
      ].filter((v) => v !== null).join("\n");

      downloadMd(md, `README-${cliente.slug}.md`);
      onArtefatoGerado("readme");
    } finally {
      setLoadingReadme(false);
    }
  }

  function gerarChecklistImplantacao() {
    const agora = new Date().toLocaleDateString("pt-BR");
    const items = implantacao.checklist || [];
    const sCfg  = IMPLANTACAO_STATUS_CFG[implantacao.status] || IMPLANTACAO_STATUS_CFG.em_andamento;

    const md = [
      `# Checklist de Implantação — ${cliente.nome}`,
      `> Gerado em: ${agora} | Status: ${sCfg.label}`,
      "",
      ...items.map((item) => {
        const check = item.concluido ? "[x]" : "[ ]";
        const extras = [
          item.data && fmtDate(item.data),
          item.responsavel,
        ].filter(Boolean).join(" · ");
        return `- ${check} ${item.label}${extras ? ` _(${extras})_` : ""}`;
      }),
    ].join("\n");

    downloadMd(md, `checklist-implantacao-${cliente.slug}.md`);
    onArtefatoGerado("checklist_implantacao");
  }

  function gerarChecklistHomologacao() {
    const agora = new Date().toLocaleDateString("pt-BR");
    const saved = Array.isArray(cliente.homologacao_checklist) ? cliente.homologacao_checklist : [];
    const status = cliente.homologacao_status || "pendente";
    const statusLabel = { pendente: "Pendente", aprovado: "Aprovado", reprovado: "Reprovado" }[status] || status;

    const md = [
      `# Checklist de Homologação — ${cliente.nome}`,
      `> Gerado em: ${agora} | Status: ${statusLabel}`,
      cliente.homologacao_responsavel ? `> Responsável: ${cliente.homologacao_responsavel}` : null,
      cliente.homologacao_data ? `> Data de aprovação: ${fmtDate(cliente.homologacao_data)}` : null,
      "",
      ...Object.entries(HOMOLOGACAO_LABELS).map(([id, label]) => {
        const ok = saved.find((s) => s.id === id)?.ok || false;
        return `- ${ok ? "[x]" : "[ ]"} ${label}`;
      }),
      ...(cliente.homologacao_notas
        ? ["", "## Observações", cliente.homologacao_notas]
        : []),
    ].filter((v) => v !== null).join("\n");

    downloadMd(md, `checklist-homologacao-${cliente.slug}.md`);
    onArtefatoGerado("checklist_homologacao");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="secondary" sm onClick={gerarReadme} disabled={loadingReadme}>
          {loadingReadme ? "Gerando..." : "⤓ README-{slug}.md"}
        </Btn>
        <Btn variant="secondary" sm onClick={gerarChecklistImplantacao}>
          ⤓ checklist-implantacao.md
        </Btn>
        <Btn variant="secondary" sm onClick={gerarChecklistHomologacao}>
          ⤓ checklist-homologacao.md
        </Btn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LinhaDoTempo
// ─────────────────────────────────────────────────────────────

function LinhaDoTempo({ clienteId }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro,  setFiltro]  = useState("todos");

  const load = useCallback(async () => {
    try {
      const data = await db(
        "fluxomed_historico", "GET", null,
        `?cliente_id=eq.${clienteId}&order=created_at.asc`,
      );
      setEventos(data || []);
    } catch (e) {
      console.error("LinhaDoTempo: erro ao carregar:", e);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  const filtrados = filtro === "implantacao"
    ? eventos.filter((ev) => EVENTOS_IMPLANTACAO.has(ev.evento))
    : eventos;

  if (loading) return <LoadingInline />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Chips
        label="Filtrar eventos"
        active={filtro}
        onChange={setFiltro}
        items={[
          { id: "todos",       label: "Todos os eventos", count: eventos.length },
          { id: "implantacao", label: "Implantação", count: eventos.filter((ev) => EVENTOS_IMPLANTACAO.has(ev.evento)).length },
        ]}
      />

      {filtrados.length === 0 ? (
        <Empty icon="◷" text="Nenhum evento registrado ainda." />
      ) : (
        <div className="klist">
          {filtrados.map((ev) => {
            const cfg = EVENTO_TIMELINE_CFG[ev.evento] || { tone: "off", label: ev.evento };
            return (
              <div key={ev.id} className="klist-row" style={{ flexWrap: "wrap" }}>
                <Selo tone={cfg.tone || "off"}>{cfg.label}</Selo>
                <span className="txt" style={{ color: "var(--muted)", fontSize: 13 }}>{ev.descricao}</span>
                <span className="aside">{fmtDateTime(ev.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TabImplantacao — componente principal (export)
// ─────────────────────────────────────────────────────────────

export function TabImplantacao({ cliente, clienteId }) {
  const [implantacao, setImplantacao] = useState(null);
  const [loading,     setLoading]     = useState(true);

  // Estado local do formulário de status
  const [status,   setStatus]   = useState("em_andamento");
  const [resp,     setResp]     = useState("");
  const [notas,    setNotas]    = useState("");
  const [saving,          setSaving]          = useState(false);
  const [saved,           setSaved]           = useState(false);
  const [refreshTimeline, setRefreshTimeline] = useState(0);

  // ── Carrega ou cria o registro de implantação ──────────────

  const load = useCallback(async () => {
    const data = await db("fluxomed_implantacao", "GET", null, `?cliente_id=eq.${clienteId}`);
    if (data && data.length > 0) {
      const rec = data[0];
      setImplantacao(rec);
      setStatus(rec.status || "em_andamento");
      setResp(rec.responsavel || "");
      setNotas(rec.notas || "");
    } else {
      // Primeira abertura — cria o registro com seed do checklist
      const [created] = await db("fluxomed_implantacao", "POST", {
        cliente_id:  clienteId,
        checklist:   CHECKLIST_SEED,
        data_inicio: new Date().toISOString(),
      });
      setImplantacao(created);
      setStatus(created.status || "em_andamento");
      // Registra evento e atualiza timeline ao concluir
      db("fluxomed_historico", "POST", {
        cliente_id: clienteId,
        evento:     "implantacao_iniciada",
        descricao:  "Processo de implantação iniciado",
      })
        .then(() => setRefreshTimeline((n) => n + 1))
        .catch((e) => console.error("[FluxoMed] implantacao_iniciada:", e));
    }
    setLoading(false);
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  // ── Salvar status, responsável e notas ────────────────────

  async function handleSaveStatus() {
    setSaving(true);
    try {
      const prevStatus = implantacao.status;
      const patch = {
        status:      status,
        responsavel: resp.trim() || null,
        notas:       notas.trim() || null,
        updated_at:  new Date().toISOString(),
        ...(status === "concluida" && prevStatus !== "concluida"
          ? { data_conclusao: new Date().toISOString() }
          : {}),
      };
      await db("fluxomed_implantacao", "PATCH", patch, `?cliente_id=eq.${clienteId}`);
      setImplantacao((prev) => ({ ...prev, ...patch }));

      if (status === "concluida" && prevStatus !== "concluida") {
        db("fluxomed_historico", "POST", {
          cliente_id: clienteId,
          evento:     "implantacao_concluida",
          descricao:  `Implantação concluída${resp.trim() ? ` por ${resp.trim()}` : ""}`,
        })
          .then(() => setRefreshTimeline((n) => n + 1))
          .catch((e) => console.error("[FluxoMed] implantacao_concluida:", e));
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  }

  // ── Atualizar item do checklist ───────────────────────────

  async function handleItemChange(updatedItem) {
    const prevItem  = implantacao.checklist.find((i) => i.id === updatedItem.id);
    const newItems  = implantacao.checklist.map((i) =>
      i.id === updatedItem.id ? updatedItem : i
    );
    await db("fluxomed_implantacao", "PATCH",
      { checklist: newItems, updated_at: new Date().toISOString() },
      `?cliente_id=eq.${clienteId}`,
    );
    setImplantacao((prev) => ({ ...prev, checklist: newItems }));

    // Registra evento somente quando item é marcado como concluído
    if (updatedItem.concluido && !prevItem?.concluido) {
      db("fluxomed_historico", "POST", {
        cliente_id: clienteId,
        evento:     "implantacao_item_concluido",
        descricao:  updatedItem.label,
        dados:      { item_id: updatedItem.id, label: updatedItem.label },
      })
        .then(() => setRefreshTimeline((n) => n + 1))
        .catch((e) => console.error("[FluxoMed] implantacao_item_concluido:", e));
    }
  }

  // ── Registrar evento de artefato gerado ──────────────────

  function handleArtefatoGerado(tipo) {
    const labels = {
      readme:                   "README",
      checklist_implantacao:    "Checklist de implantação",
      checklist_homologacao:    "Checklist de homologação",
    };
    db("fluxomed_historico", "POST", {
      cliente_id: clienteId,
      evento:     "artefato_gerado",
      descricao:  `${labels[tipo] || tipo} gerado: ${tipo.replace(/_/g, "-")}-${cliente.slug}.md`,
      dados:      { tipo },
    })
      .then(() => setRefreshTimeline((n) => n + 1))
      .catch((e) => console.error("[FluxoMed] artefato_gerado:", e));
  }

  // ── Render ────────────────────────────────────────────────

  if (loading) return <LoadingInline />;

  const sCfg = IMPLANTACAO_STATUS_CFG[implantacao.status] || IMPLANTACAO_STATUS_CFG.em_andamento;

  return (
    <div className="tab-pane" style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>

      <Explica title="Etapa final antes da produção: importar no n8n, conectar o WhatsApp e treinar a equipe">
        Após a homologação técnica, esta aba guia a implantação operacional.
        Só marque o status como <strong>Produção</strong> no cliente após concluir todos os itens.
      </Explica>

      {/* ── Status da implantação ─────────────────────────── */}
      <Section
        title="Status da implantação"
        sub={implantacao.data_inicio
          ? `Início: ${fmtDate(implantacao.data_inicio)}${implantacao.data_conclusao ? ` · Concluído: ${fmtDate(implantacao.data_conclusao)}` : ""}`
          : undefined}
      >
        <div><Selo tone={sCfg.tone}>{sCfg.label}</Selo></div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label className="field-label">Status</label>
              <div className="select-wrap">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="select"
                >
                  {Object.entries(IMPLANTACAO_STATUS_CFG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label className="field-label">Responsável</label>
              <input
                className="input"
                value={resp}
                onChange={(e) => setResp(e.target.value)}
                placeholder="Ex: Rafael"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="field">
            <label className="field-label">Notas</label>
            <textarea
              className="textarea"
              rows={2}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Observações sobre a implantação..."
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Btn variant="primary" sm onClick={handleSaveStatus} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Btn>
            {saved && <Selo tone="ok" dot={false}>✓ Salvo</Selo>}
          </div>
        </div>
      </Section>

      {/* ── Checklist de implantação ─────────────────────── */}
      <Section title="Checklist de implantação">
        <ChecklistImplantacao
          items={implantacao.checklist || []}
          onItemChange={handleItemChange}
        />
      </Section>

      {/* ── Artefatos ────────────────────────────────────── */}
      <Section title="Artefatos" sub={`Gerados no navegador; nada sai para servidor. Para o n8n, use o config-${cliente.slug}.json da aba Exportar.`}>
        <ArtefatosPanel
          cliente={cliente}
          clienteId={clienteId}
          implantacao={implantacao}
          onArtefatoGerado={handleArtefatoGerado}
        />
      </Section>

      {/* ── Linha do tempo ───────────────────────────────── */}
      <Section title="Linha do tempo">
        <LinhaDoTempo key={refreshTimeline} clienteId={clienteId} />
      </Section>

    </div>
  );
}
